import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import {
  getUAEDateString,
  getUAETimeString,
} from '../components/UAEClock';
import { Calendar } from 'lucide-react';

import AddRevenueDialog from '../components/revenues/AddRevenueDialog';
import EditRevenueDialog from '../components/revenues/EditRevenueDialog';
import RevenueReceiptModal from '../components/revenues/RevenueReceiptModal';
import RevenueStatsCards from '../components/revenues/RevenueStatsCards';
import RevenuesTable from '../components/revenues/RevenuesTable';
import AttachLoyaltyDialog from '../components/revenues/AttachLoyaltyDialog';
import useLoyaltyForRevenue from '../hooks/useLoyaltyForRevenue';
import useReceiptPrint from '../hooks/useReceiptPrint';

const MyRevenues = () => {
  // eslint-disable-next-line no-unused-vars
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { user, isBarber, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [barbers, setBarbers] = useState([]);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getUAEDateString());
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedRevenues, setSelectedRevenues] = useState([]);
  const [sendingToAdmin, setSendingToAdmin] = useState(false);
  const [timeWarning, setTimeWarning] = useState('');
  const [attachLoyaltyRevenue, setAttachLoyaltyRevenue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);  // prevents double-submit

  // Receipt printing handled by hook
  const {
    printRef: receiptPrintRef,
    receiptData,
    setReceiptData,
    receiptLanguage,
    setReceiptLanguage,
    bilingualReceipt,
    setBilingualReceipt,
    printingReceiptId: printingReceipt,
    printReceipt,
    triggerPrint: handleReceiptPrint,
  } = useReceiptPrint(language);

  // Loyalty handled by hook
  const loyalty = useLoyaltyForRevenue(language);
  const loyaltyMember = loyalty.member;
  const setLoyaltyMember = loyalty.setMember;
  const lookingUpLoyalty = loyalty.looking;
  const redeemPoints = loyalty.redeemPoints;
  const setRedeemPoints = loyalty.setRedeemPoints;
  const lookupLoyaltyMember = loyalty.lookup;

  const [editFormData, setEditFormData] = useState({
    amount: '',
    client_count: '1',
    payment_method: 'cash',
    date: '',
    description: '',
  });
  const [formData, setFormData] = useState({
    amount: '',
    client_count: '1',
    payment_method: 'cash',
    receipt_date: getUAEDateString(),
    receipt_time: getUAETimeString(),
    description: '',
    membership_number: '',
  });

  const calculatedDay = useMemo(() => {
    if (!formData.receipt_date) return '';
    try {
      const date = new Date(formData.receipt_date);
      const days =
        language === 'ar'
          ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
          : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[date.getDay()];
    } catch {
      return '';
    }
  }, [formData.receipt_date, language]);

  const validateTime = (inputTime, inputDate) => {
    if (isAdmin()) return true;
    try {
      const now = new Date();
      const inputDateTime = new Date(`${inputDate}T${inputTime}:00`);
      const diffMs = Math.abs(now - inputDateTime);
      const diffMinutes = diffMs / (1000 * 60);
      return diffMinutes <= 60;
    } catch {
      return false;
    }
  };

  const handleTimeChange = (newTime) => {
    setFormData((prev) => ({ ...prev, receipt_time: newTime }));
    if (!isAdmin() && !validateTime(newTime, formData.receipt_date)) {
      setTimeWarning(
        language === 'ar'
          ? '⚠️ الوقت المدخل يتجاوز ±1 ساعة من الوقت الحالي. لن يتم حفظ الإيراد.'
          : '⚠️ The entered time exceeds ±1 hour from current time. Revenue will not be saved.'
      );
    } else {
      setTimeWarning('');
    }
  };

  const handleDateChange = (newDate) => {
    setFormData((prev) => ({ ...prev, receipt_date: newDate }));
    if (!isAdmin() && !validateTime(formData.receipt_time, newDate)) {
      setTimeWarning(
        language === 'ar'
          ? '⚠️ الوقت المدخل يتجاوز ±1 ساعة من الوقت الحالي. لن يتم حفظ الإيراد.'
          : '⚠️ The entered time exceeds ±1 hour from current time. Revenue will not be saved.'
      );
    } else {
      setTimeWarning('');
    }
  };

  // Auto-update time and date every minute when dialog is closed
  useEffect(() => {
    const interval = setInterval(() => {
      if (!open) {
        const newDate = getUAEDateString();
        const newTime = getUAETimeString();
        setFormData((prev) => ({ ...prev, receipt_date: newDate, receipt_time: newTime }));
        setSelectedDate(newDate);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    fetchBarbers();
    if (!isAdmin()) {
      fetchMyStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedBarber && isAdmin()) {
      fetchBarberStats(selectedBarber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBarber, selectedPeriod, selectedDate]);

  const fetchBarbers = async () => {
    try {
      const res = await api.get('/barbers');
      setBarbers(res.data);
      if (res.data.length > 0 && isAdmin()) {
        setSelectedBarber(res.data[0].id);
      }
    } catch {
      console.log('فشل تحميل قائمة الحلاقين');
    }
  };

  const fetchMyStats = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/revenues/my-stats?period=${selectedPeriod}`);
      setStats(res.data);
    } catch {
      toast.error('فشل تحميل الإحصائيات');
    } finally {
      setLoading(false);
    }
  };

  const fetchBarberStats = async (barberId) => {
    setLoading(true);
    try {
      const res = await api.get(`/revenues/barber/${barberId}/daily-stats?date=${selectedDate}`);
      setStats(res.data);
    } catch {
      toast.error('فشل تحميل الإحصائيات');
    } finally {
      setLoading(false);
    }
  };

  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'today':
        return 'اليوم';
      case 'last_2_days':
        return 'آخر يومين';
      case 'last_week':
        return 'هذا الأسبوع';
      default:
        return 'اليوم';
    }
  };

  const canEditRevenue = (revenue) => {
    try {
      const createdAt = new Date(revenue.created_at || revenue.date);
      const now = new Date();
      const hourInMs = 60 * 60 * 1000;
      return now - createdAt < hourInMs;
    } catch {
      return false;
    }
  };

  const canPrintReceipt = (revenue) => {
    if (isAdmin()) return true;
    try {
      const revenueDate = new Date(revenue.date);
      const now = new Date();
      const dayInMs = 24 * 60 * 60 * 1000;
      const withinOneDay = now - revenueDate < dayInMs;
      const underPrintLimit = (revenue.print_count || 0) < 2;
      return withinOneDay && underPrintLimit;
    } catch {
      return false;
    }
  };

  const toggleRevenueSelection = (revenueId) => {
    setSelectedRevenues((prev) =>
      prev.includes(revenueId) ? prev.filter((id) => id !== revenueId) : [...prev, revenueId]
    );
  };

  const toggleSelectAll = () => {
    if (!stats?.revenues) return;
    const pending = stats.revenues.filter(
      (r) => r.submission_status !== 'sent' && r.submission_status !== 'مُرسل'
    );
    if (selectedRevenues.length === pending.length) setSelectedRevenues([]);
    else setSelectedRevenues(pending.map((r) => r.id));
  };

  const handleSendToAdmin = async () => {
    if (selectedRevenues.length === 0) {
      toast.error('يرجى تحديد إيراد واحد على الأقل');
      return;
    }
    setSendingToAdmin(true);
    try {
      const res = await api.put('/revenues/send-to-admin', { revenue_ids: selectedRevenues });
      toast.success(res.data.message || 'تم إرسال الإيرادات بنجاح');
      setSelectedRevenues([]);
      if (isAdmin()) fetchBarberStats(selectedBarber);
      else fetchMyStats();
    } catch {
      toast.error('فشل إرسال الإيرادات');
    } finally {
      setSendingToAdmin(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double-submission (rapid clicks / Enter spam → duplicate revenues)
    if (isSubmitting) return;

    if (!isAdmin() && !validateTime(formData.receipt_time, formData.receipt_date)) {
      try {
        await api.post('/barber-violations/log', {
          violation_type: 'invalid_time',
          details: {
            attempted_date: formData.receipt_date,
            attempted_time: formData.receipt_time,
            current_time: getUAETimeString(),
          },
        });
      } catch {
        console.log('Failed to log violation');
      }
      toast.error(
        language === 'ar'
          ? 'لا يمكن إضافة إيراد بوقت يتجاوز ±1 ساعة من الوقت الحالي'
          : 'Cannot add revenue with time exceeding ±1 hour from current time'
      );
      return;
    }

    try {
      setIsSubmitting(true);
      let barberId;
      if (isAdmin()) {
        barberId = selectedBarber;
      } else {
        barberId = user?.barber_id || stats?.barber_id;
        if (!barberId && barbers.length > 0) {
          const myBarber = barbers.find((b) => b.user_id === user?.id || b.email === user?.email);
          barberId = myBarber?.id;
        }
      }
      if (isAdmin() && !barberId) {
        toast.error(language === 'ar' ? 'يرجى اختيار الحلاق' : 'Please select a barber');
        return;
      }

      const payload = {
        amount: parseFloat(formData.amount),
        client_count: parseInt(formData.client_count),
        payment_method: formData.payment_method,
        receipt_time: formData.receipt_time,
        date: new Date(`${formData.receipt_date}T${formData.receipt_time}:00`).toISOString(),
        description: formData.description || null,
        // Idempotency key — guarantees the backend rejects duplicate submissions
        client_request_id: (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };
      if (barberId) payload.barber_id = barberId;

      const redeemQty = Math.min(parseInt(redeemPoints) || 0, loyaltyMember?.points_balance || 0);
      const redeemAed = redeemQty * 0.07;
      const netAmount = Math.max(0, payload.amount - redeemAed);
      const payloadAdjusted = { ...payload, amount: netAmount };
      if (redeemQty > 0) {
        payloadAdjusted.original_amount = payload.amount;
        payloadAdjusted.loyalty_redeem_points = redeemQty;
        payloadAdjusted.loyalty_redeem_aed = redeemAed;
        payloadAdjusted.description =
          (formData.description ? formData.description + ' — ' : '') +
          `استبدال ${redeemQty} نقطة (-${redeemAed.toFixed(2)} د.إ)`;
      }
      // Pass loyalty member context so the receipt can show full breakdown
      if (loyaltyMember && loyaltyMember.id) {
        payloadAdjusted.loyalty_membership_number = loyaltyMember.membership_number;
        payloadAdjusted.loyalty_member_name = loyaltyMember.name;
        if (netAmount > 0) {
          const basePts = Math.floor(netAmount);
          const bonusPct = loyaltyMember.level?.bonus_pct || 0;
          payloadAdjusted.loyalty_points_earned = basePts + Math.floor((basePts * bonusPct) / 100);
        }
      }

      const revenueRes = await api.post('/revenues', payloadAdjusted);
      const createdRevenueId = revenueRes?.data?.id;

      // Strict rule: a loyalty card cannot earn or redeem until the admin approves it.
      // For pending memberships, we only register a receipt-attachment; the points
      // will be credited retroactively when the admin approves the membership.
      const memberIsApproved = !!(loyaltyMember && loyaltyMember.can_earn !== false && !loyaltyMember.pending_member);

      if (memberIsApproved && loyaltyMember && loyaltyMember.id && redeemQty > 0) {
        await loyalty.redeem(
          loyaltyMember.id,
          redeemQty,
          `استبدال نقاط مع إيراد ${formData.description || ''}`.trim()
        );
      }

      if (memberIsApproved && loyaltyMember && loyaltyMember.id && netAmount > 0) {
        await loyalty.award(loyaltyMember.id, netAmount, {
          service_name: formData.description,
          amount_aed: netAmount,
        });
      }

      // For "recent" memberships (< 3 days) OR any PENDING membership,
      // create a pending receipt-attachment requiring admin approval within 7 days.
      // Points get credited retroactively on approval.
      const needsAttachment = loyaltyMember && loyaltyMember.id && createdRevenueId &&
        (loyaltyMember.is_recent || loyaltyMember.pending_member);
      if (needsAttachment) {
        await loyalty.createReceiptAttachment({
          revenue_id: createdRevenueId,
          member_id: loyaltyMember.id,
          membership_number: loyaltyMember.membership_number,
          member_name: loyaltyMember.name,
          barber_id: barberId,
          barber_name: barbers.find((b) => b.id === barberId)?.name || user?.name,
          amount: netAmount,
          points_earned: payloadAdjusted.loyalty_points_earned || Math.floor(netAmount),
        });
      }

      toast.success(language === 'ar' ? 'تم إضافة الإيراد بنجاح' : 'Revenue added successfully');
      setOpen(false);
      setTimeWarning('');
      setSelectedDate(getUAEDateString());
      setFormData({
        amount: '',
        client_count: '1',
        payment_method: 'cash',
        receipt_date: getUAEDateString(),
        receipt_time: getUAETimeString(),
        description: '',
        membership_number: '',
      });
      setLoyaltyMember(null);
      setRedeemPoints('');
      loyalty.reset();

      if (isAdmin()) fetchBarberStats(selectedBarber);
      else fetchMyStats();
    } catch {
      toast.error(language === 'ar' ? 'فشل إضافة الإيراد' : 'Failed to add revenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (revenueId) => {
    if (!isAdmin()) {
      toast.error('هذه الصلاحية للمدير فقط');
      return;
    }
    if (!window.confirm('سيتم نقل الإيراد إلى سلة المحذوفات (يمكن استعادته خلال 7 أيام). متابعة؟')) return;
    try {
      const res = await api.delete(`/revenues/${revenueId}`);
      toast.success(res.data?.message || 'تم النقل إلى سلة المحذوفات');
      if (isAdmin()) fetchBarberStats(selectedBarber);
      else fetchMyStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل حذف الإيراد');
    }
  };

  const handleEdit = (revenue) => {
    setEditingRevenue(revenue);
    setEditFormData({
      amount: revenue.amount?.toString() || '',
      client_count: (revenue.client_count || revenue.customers || 1).toString(),
      payment_method: revenue.payment_method || 'cash',
      date: revenue.date?.substring(0, 10) || getUAEDateString(),
      description: revenue.description || '',
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingRevenue) return;
    try {
      const payload = {
        amount: parseFloat(editFormData.amount),
        client_count: parseInt(editFormData.client_count),
        payment_method: editFormData.payment_method,
        date: editFormData.date,
        description: editFormData.description || null,
      };
      await api.put(`/revenues/${editingRevenue.id}`, payload);
      toast.success('تم تعديل الإيراد بنجاح');
      setEditOpen(false);
      setEditingRevenue(null);
      fetchMyStats();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'فشل تعديل الإيراد';
      toast.error(typeof errorMsg === 'object' ? errorMsg.ar || errorMsg.en : errorMsg);
    }
  };

  if (!isBarber() && !isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{isAdmin() ? 'إيرادات الحلاقين' : 'إيراداتي'}</h1>
          <p className="text-muted-foreground">
            {stats?.barber_name || user?.name} - {getPeriodLabel()}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {isBarber() && (
            <div className="flex items-center gap-2 bg-secondary/30 rounded-lg p-1">
              {[
                { key: 'today', label: 'اليوم' },
                { key: 'last_2_days', label: 'آخر يومين' },
                { key: 'last_week', label: 'هذا الأسبوع' },
              ].map((p) => (
                <button
                  key={p.key}
                  onClick={() => setSelectedPeriod(p.key)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedPeriod === p.key
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary/50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          {isAdmin() && (
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-secondary/50 border-white/10 w-40"
              />
            </div>
          )}

          {isAdmin() && barbers.length > 0 && (
            <select
              value={selectedBarber || ''}
              onChange={(e) => setSelectedBarber(e.target.value)}
              className="bg-secondary/50 border border-white/10 rounded-lg px-4 py-2"
            >
              {barbers.map((barber) => (
                <option key={barber.id} value={barber.id}>
                  {barber.name}
                </option>
              ))}
            </select>
          )}

          <AddRevenueDialog
            open={open}
            onOpenChange={(isOpen) => {
              setOpen(isOpen);
              if (isOpen) {
                setFormData((prev) => ({
                  ...prev,
                  receipt_date: getUAEDateString(),
                  receipt_time: getUAETimeString(),
                }));
                setSelectedDate(getUAEDateString());
              }
            }}
            language={language}
            formData={formData}
            setFormData={setFormData}
            calculatedDay={calculatedDay}
            handleDateChange={handleDateChange}
            handleTimeChange={handleTimeChange}
            timeWarning={timeWarning}
            loyaltyMember={loyaltyMember}
            setLoyaltyMember={setLoyaltyMember}
            lookingUpLoyalty={lookingUpLoyalty}
            lookupLoyaltyMember={lookupLoyaltyMember}
            redeemPoints={redeemPoints}
            setRedeemPoints={setRedeemPoints}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>

      <RevenueStatsCards stats={stats} />

      <RevenuesTable
        stats={stats}
        isAdmin={isAdmin}
        selectedRevenues={selectedRevenues}
        toggleRevenueSelection={toggleRevenueSelection}
        toggleSelectAll={toggleSelectAll}
        handleSendToAdmin={handleSendToAdmin}
        sendingToAdmin={sendingToAdmin}
        printingReceipt={printingReceipt}
        handlePrintReceipt={printReceipt}
        canPrintReceipt={canPrintReceipt}
        canEditRevenue={canEditRevenue}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        getPeriodLabel={getPeriodLabel}
        onAttachLoyalty={setAttachLoyaltyRevenue}
      />

      <AttachLoyaltyDialog
        open={!!attachLoyaltyRevenue}
        onOpenChange={(v) => !v && setAttachLoyaltyRevenue(null)}
        revenue={attachLoyaltyRevenue}
        onSuccess={() => {
          setAttachLoyaltyRevenue(null);
          if (isAdmin()) fetchBarberStats(selectedBarber);
          else fetchMyStats();
        }}
      />

      <EditRevenueDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        formData={editFormData}
        onChange={setEditFormData}
        onSubmit={handleEditSubmit}
      />

      <RevenueReceiptModal
        receiptData={receiptData}
        language={language}
        receiptLanguage={receiptLanguage}
        setReceiptLanguage={setReceiptLanguage}
        bilingualReceipt={bilingualReceipt}
        setBilingualReceipt={setBilingualReceipt}
        receiptPrintRef={receiptPrintRef}
        onPrint={handleReceiptPrint}
        onClose={() => setReceiptData(null)}
      />
    </div>
  );
};

export default MyRevenues;
